<script setup>
import { ref, computed } from 'vue';
import { useForm, useField } from 'vee-validate';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import IModal from '../IModal/IModal.vue';
import IInput from '../IInput/IInput.vue';
import useCartStore from '../../store/modules/cart.js';
import validationSchema from '../../composables/validationSchema.js';
import api from "@shop/frontend/web/js/api";

const cartStore = useCartStore();

translator.getLanguages();

const agreeState = ref(false);

const actionLink = computed(() => {
  if (process.env.NODE_ENV === 'development') {
    return `/user/registration/fast-register?event_id=${cartStore.getcurrentOpenedEventId.value}&type=${cartStore.eventTypeToPay.value}`;
  }

  const lang = document.querySelector('html').getAttribute('lang') ?? 'ru';
  return `/${lang}/user/registration/fast-register?event_id=${cartStore.getcurrentOpenedEventId.value}&type=${cartStore.eventTypeToPay.value}`;
});

function closeFastBuyModal() {
  cartStore.setFastBuyModalStatus(false);
}

// TODO перенести получение csrfToken в stateManager
const csrfToken = document.head.querySelector('meta[name="csrf-token"]');

const {
  values,
  errors,
  setErrors,
  defineInputBinds,
  handleSubmit,
  setFieldValue,
} = useForm({
  validationSchema,
});

const email = defineInputBinds('email');
const first = defineInputBinds('first');
const second = defineInputBinds('second');
const middle = defineInputBinds('middle');
const phone = defineInputBinds('phone');
const agree = defineInputBinds('agree');

const middleField = useField('middle');

const preparePhone = () => {
  if (!values.phone) {
    setFieldValue('phone', 'null');
  }
};

const redirectUri = ref(`${window.location.origin}/social/fast-auth`);

const submitForm = handleSubmit ((value) => {
  const formObject = {
    email: email.value.value,
    first: first.value.value,
    second: second.value.value,
    middle: middle.value.value,
    phone: phone.value.value,
    agree: agree.value.value,
  };
  const fieldsForm = validationSchema._nodes;

  api.user.fastRegisterAjax({eventId: cartStore.getcurrentOpenedEventId.value, type: cartStore.eventTypeToPay.value, FastRegistrationForm: formObject, csrf: csrfToken.content, ajax: 'fast-registration-controller' })
  .then((res) => {
    if (res.redirectUrl) {
      window.location.replace(res.redirectUrl)
    }
    
    fieldsForm.forEach((value) => {
      setErrors({
        [value]: res[value],
      })
    })
  })
})
</script>

<template>
  <i-modal modal-class="user-modal fast-register-modal" @hide="closeFastBuyModal()">
    <template #header>
      <span class="modal-title-header">
        {{ translator.t("main", "Fast pay") }}
      </span>
      <!-- TODO: Убрать data-target и класс js-get-modal. Переделать на vue -->
      <button class="modal-title-btn js-get-modal" data-target="#loginModal">
        {{ translator.t("main", "Sign in") }}
      </button>
    </template>
    <template #body>
      <div class="modal-body-info">
        {{
          translator.t(
            "main",
            "Fill in all fields to proceed to the purchase. After payment, tickets will be sent to the specified e-mail."
          )
        }}
      </div>
      <form :action="actionLink" method="post" autocomplete="off" @submit="submitForm">
        <fieldset>
          <input type="hidden" name="_csrf-frontend" :value="csrfToken.content">
          <div class="user-modal__input-wrapper_with-info">
            <i-input
              type="email"
              :placeholder="translator.t('user', 'E-mail')"
              input-id="fastregistrationform-email"
              :is-label="true"
              :is-required="true"
              :bind-name="email"
              wrapper-class="user-modal__input_with-info"
              name="FastRegistrationForm[email]"
            />
            <div v-if="errors.email" class="user-modal__input-error">
              {{ errors.email }}
            </div>
          </div>
          <p class="user-modal__input-info">
            {{ translator.t("main", "Check email") }}
          </p>
        </fieldset>

        <fieldset>
          <div class="user-modal__input-wrapper">
            <i-input
              type="text"
              :placeholder="translator.t('user', 'Name')"
              input-id="fastregistrationform-name"
              :is-label="true"
              :is-required="true"
              :bind-name="first"
              wrapper-class="user-modal__input"
              name="FastRegistrationForm[first]"
            />
            <div v-if="errors.first" class="user-modal__input-error">
              {{ errors.first }}
            </div>
          </div>

          <div class="user-modal__input-wrapper">
            <i-input
              type="text"
              :placeholder="translator.t('user', 'Surname')"
              input-id="fastregistrationform-surname"
              :is-label="true"
              :is-required="true"
              :bind-name="second"
              wrapper-class="user-modal__input"
              name="FastRegistrationForm[second]"
            />
            <div v-if="errors.second" class="user-modal__input-error">
              {{ errors.second }}
            </div>
          </div>

          <div class="user-modal__input-wrapper_with-info">
            <i-input
              type="text"
              :placeholder="translator.t('user', 'Patronymic')"
              input-id="fastregistrationform-middle"
              :is-label="true"
              :is-required="false"
              :bind-name="middle"
              wrapper-class="user-modal__input_with-info"
              name="FastRegistrationForm[middle]"
              @input="values.middle === '' ? middleField.handleReset() : null"
              @change="values.middle === '' ? middleField.handleReset() : null"
            />
            <div v-if="errors.middle" class="user-modal__input-error">
              {{ errors.middle }}
            </div>
          </div>
          <p class="user-modal__input-info">
            {{ translator.t("main", "We recommend you to fill in if available") }}
          </p>

          <div class="user-modal__input-wrapper">
            <i-input
              type="tel"
              :placeholder="translator.t('user', 'Phone')"
              mask="+7(###)###-##-##"
              input-id="fastregistrationform-tel"
              :is-label="true"
              :is-required="true"
              :bind-name="phone"
              wrapper-class="user-modal__input"
              name="FastRegistrationForm[phone]"
              @focused="preparePhone"
            />
            <div v-if="errors.phone" class="user-modal__input-error">
              {{ errors.phone }}
            </div>
          </div>
        </fieldset>

        <div class="modal-body-info">
          {{ translator.t("main", "Or use the auto-complete form using social media.") }}
        </div>
        <div class="social social_enter">
          <div
            id="uLogin"
            :data-ulogin="
              `display=panel;
              theme=classic;
              fields=first_name,last_name,email;
              providers=vkontakte,google;
              redirect_uri=${redirectUri};mobilebuttons=0;`
            "
          />
        </div>

        <div class="user-modal__input-wrapper user-modal__input-wrapper_agree">
          <label class="user-modal__agree-label">
            <input
              id="fastregistrationform-agree"
              v-bind="agree"
              v-model="agreeState"
              :value="agreeState ? 1 : 0"
              class="custom-checkbox-input"
              name="FastRegistrationForm[agree]"
              type="checkbox"
            >
            <span class="custom-checkbox" />
            <span class="user-modal__agree-text">
              {{ translator.t("main", "I have read the") }}
              <a href="">{{ translator.t("main", "User Agreement") }}</a>,
              {{ translator.t("main", "accept it and give my") }}
              <a href="">{{
                translator.t("main", "Consent to the processing of personal data")
              }}</a>
            </span>
          </label>
          <div v-if="errors.agree" class="user-modal__input-error">
            {{ errors.agree }}
          </div>
        </div>

        <button class="btn modal-body-button" type="submit">
          {{ translator.t("main", "Go to buy") }}
        </button>
      </form>
    </template>
  </i-modal>
</template>

<style lang="scss">

</style>
